import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import "./index.css";
import { Impressum } from "./Impressum";
import { Privacy } from "./Privacy";
import { Testimonials } from "./Testimonials";
import { Manifesto } from "./Manifesto";
import { BlogIndex, BlogPostPage, ReleaseArticlePage, navigate } from "./BlogView";
import { useT } from "./i18n/context";
import { getDownloadInfo, useLatestVersion } from "./download";
import { LanguageSwitcher } from "./i18n/LanguageSwitcher";
import heroBg from "./assets/hero-bg.jpg";
import demoScreen01 from "./assets/demo/hero-screen-01.webp";
import demoScreen02 from "./assets/demo/hero-screen-02.webp";
import demoScreen03 from "./assets/demo/hero-screen-03.webp";
import demoScreen04 from "./assets/demo/hero-screen-04.webp";
import demoScreen05 from "./assets/demo/hero-screen-05.webp";
import demoScreen06 from "./assets/demo/hero-screen-06.webp";
import demoScreen07 from "./assets/demo/hero-screen-07.webp";
import demoScreen08 from "./assets/demo/hero-screen-08.webp";
import demoScreen09 from "./assets/demo/hero-screen-09.webp";
import demoScreen10 from "./assets/demo/hero-screen-10.webp";
import demoScreen11 from "./assets/demo/hero-screen-11.webp";
import demoScreen12 from "./assets/demo/hero-screen-12.webp";
import demoScreen13 from "./assets/demo/hero-screen-13.webp";
import demoScreen14 from "./assets/demo/hero-screen-14.webp";
import demoScreen15 from "./assets/demo/hero-screen-15.webp";
import demoScreen16 from "./assets/demo/hero-screen-16.webp";
import demoScreen17 from "./assets/demo/hero-screen-17.webp";
import demoScreen18 from "./assets/demo/hero-screen-18.webp";
import demoScreen19 from "./assets/demo/hero-screen-19.webp";
import demoScreen20 from "./assets/demo/hero-screen-20.webp";
import demoScreen21 from "./assets/demo/hero-screen-21.webp";
import demoScreen22 from "./assets/demo/hero-screen-22.webp";
import demoScreen23 from "./assets/demo/hero-screen-23.webp";
import demoScreen24 from "./assets/demo/hero-screen-24.webp";

// Captures of the actual Cate app window, with its native chrome and rounded corners.
const demoShots = [
  { src: demoScreen01, label: "Dark Neutral" },
  { src: demoScreen02, label: "Dark Cold" },
  { src: demoScreen03, label: "Dark Warm" },
  { src: demoScreen04, label: "Light Subtle" },
  { src: demoScreen05, label: "Dracula" },
  { src: demoScreen06, label: "Nord" },
  { src: demoScreen07, label: "Solarized Dark" },
  { src: demoScreen08, label: "Tokyo Night" },
  { src: demoScreen09, label: "Visual Studio Dark" },
  { src: demoScreen10, label: "Visual Studio Light" },
  { src: demoScreen11, label: "One Dark Pro" },
  { src: demoScreen12, label: "One Dark Pro Darker" },
  { src: demoScreen13, label: "One Dark Pro Flat" },
  { src: demoScreen14, label: "One Dark Pro Vivid" },
  { src: demoScreen15, label: "One Light" },
  { src: demoScreen16, label: "Clay Light" },
  { src: demoScreen17, label: "Clay Dark" },
  { src: demoScreen18, label: "IntelliJ Light" },
  { src: demoScreen19, label: "True Black Minimal" },
  { src: demoScreen20, label: "Moss Graphite" },
  { src: demoScreen21, label: "Tokyo Night Cinnabar" },
  { src: demoScreen22, label: "Dracula, sidebar hidden" },
  { src: demoScreen23, label: "Dracula, right split" },
  { src: demoScreen24, label: "Light Subtle, right split" },
];

type Theme = "dark" | "light";

function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem("cate:theme");
    if (stored === "light" || stored === "dark") return stored;
  } catch {}
  return "dark";
}

function applyTheme(theme: Theme) {
  if (theme === "light") {
    document.documentElement.setAttribute("data-theme", "light");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
  try { localStorage.setItem("cate:theme", theme); } catch {}
}

function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => { applyTheme(theme); }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggle };
}

function ThemeToggle({ theme, onToggle }: { theme: Theme; onToggle: () => void }) {
  const { common } = useT();
  return (
    <button
      onClick={onToggle}
      aria-label={theme === "dark" ? common.aria.themeToLight : common.aria.themeToDark}
      className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
      style={{ color: "inherit", opacity: 0.7 }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
    >
      {theme === "light" ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.17 3.17l1.06 1.06M11.77 11.77l1.06 1.06M3.17 12.83l1.06-1.06M11.77 4.23l1.06-1.06" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M13.5 10.07A6.5 6.5 0 015.93 2.5a6 6 0 107.57 7.57z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

function Nav({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const { common } = useT();
  const navColor = theme === "dark" ? "#fff" : "var(--text-primary)";
  const linkColor = theme === "dark" ? "rgba(255,255,255,0.7)" : "var(--text-muted)";
  const linkHover = theme === "dark" ? "#fff" : "var(--text-primary)";
  return (
    <nav className="fixed top-0 left-0 right-0" style={{ color: navColor, zIndex: 999 }}>
      <div className="mx-auto px-4 sm:px-8 md:px-12 h-14 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            if (window.location.pathname !== "/") {
              window.history.pushState(null, "", "/");
              window.dispatchEvent(new PopStateEvent("popstate"));
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <svg viewBox="0 0 389 204" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5">
            <path d="M274 203.2L307.29 1.79999H388.29L384.51 24.84H329.97L320.5 80.16H342.22H366.34L362.74 103.2H338.62H316.5L304.06 180.16H358.6L355 203.2H314.5H274Z" fill="currentColor"/>
            <path d="M201.264 203.2L230.424 26.5H197.124L201.264 1.3H294.864L290.724 26.5H257.424L228.264 203.2H201.264Z" fill="currentColor"/>
            <path d="M89 133.2L142.1 1.79999H176.3L188 133.2H161.18L159.56 103.5H128.24L117.26 133.2H89ZM136.16 81.9H158.3L157.04 50.22C156.92 45.66 156.68 41.16 156.32 36.72C156.08 32.16 155.9 28.62 155.78 26.1C154.94 28.62 153.8 32.1 152.36 36.54C151.04 40.98 149.54 45.48 147.86 50.04L136.16 81.9Z" fill="currentColor"/>
            <path d="M38.1825 135C29.4225 135 21.9825 133.38 15.8625 130.14C9.7425 126.78 5.3625 122.16 2.7225 116.28C0.0824997 110.28 -0.6375 103.32 0.5625 95.4L9.3825 39.6C10.7025 31.56 13.6425 24.6 18.2025 18.72C22.7625 12.84 28.5825 8.27999 35.6625 5.04C42.8625 1.68 50.8425 0 59.6025 0C68.4825 0 75.9225 1.68 81.9225 5.04C87.9225 8.27999 92.3025 12.84 95.0625 18.72C97.8225 24.6 98.5425 31.56 97.2225 39.6H70.2225C71.1825 34.32 70.4025 30.3 67.8825 27.54C65.3625 24.78 61.4025 23.4 56.0025 23.4C50.6025 23.4 46.2225 24.78 42.8625 27.54C39.5025 30.3 37.3425 34.32 36.3825 39.6L27.5625 95.4C26.7225 100.56 27.5625 104.58 30.0825 107.46C32.6025 110.22 36.5625 111.6 41.9625 111.6C47.3625 111.6 51.7425 110.22 55.1025 107.46C58.4625 104.58 60.5625 100.56 61.4025 95.4H88.4025C87.2025 103.32 84.2625 110.28 79.5825 116.28C75.0225 122.16 69.2025 126.78 62.1225 130.14C55.0425 133.38 47.0625 135 38.1825 135Z" fill="currentColor"/>
          </svg>
        </a>
        <div className="flex items-center gap-3 sm:gap-6">
          <a
            href="/blog"
            onClick={(e) => {
              e.preventDefault();
              (window as any).__analytics?.track("blog_click", { location: "nav" });
              navigate("/blog");
            }}
            className="text-sm transition-colors flex items-center gap-1.5"
            style={{ color: linkColor }}
            onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
          >
            {common.nav.blog}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </a>
          <a
            href="https://github.com/0-AI-UG/cate"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => (window as any).__analytics?.track("github_click")}
            className="text-sm transition-colors flex items-center gap-1.5"
            style={{ color: linkColor }}
            onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
          >
            {common.nav.github}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </a>
          <a
            href="https://www.producthunt.com/products/cate?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-cate"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex text-sm transition-colors items-center gap-1.5"
            style={{ color: linkColor }}
            onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = linkColor)}
          >
            {common.nav.productHunt}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </a>
          <div className="flex items-center gap-1">
            <LanguageSwitcher theme={theme} />
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          </div>
        </div>
      </div>
    </nav>
  );
}

function HeroNewsletter() {
  const { home } = useT();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) {
        setStatus("done");
        setEmail("");
        (window as any).__analytics?.track("newsletter_subscribe", { location: "hero" });
      }
    } catch {}
  };

  if (status === "done") {
    return (
      <p
        className="fade-up text-sm mt-6"
        style={{ color: "var(--text-primary)", opacity: 0.8, animationDelay: "0.35s" }}
      >
        {home.heroNewsletter.done}
      </p>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="fade-up flex items-center justify-center gap-2 mt-6 px-4 mx-auto"
      style={{ animationDelay: "0.35s", maxWidth: 320 }}
    >
      <input
        type="email"
        required
        placeholder={home.heroNewsletter.placeholder}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="px-3 py-2 rounded-full text-xs outline-none flex-1 min-w-0"
        style={{
          backgroundColor: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "var(--text-primary)",
          backdropFilter: "blur(8px)",
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="px-3 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap"
        style={{
          backgroundColor: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "var(--text-primary)",
          opacity: status === "loading" ? 0.6 : 0.8,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
      >
        {home.heroNewsletter.subscribe}
      </button>
    </form>
  );
}

function Hero({ theme }: { theme: Theme }) {
  const { home } = useT();
  const version = useLatestVersion();
  const download = useMemo(() => getDownloadInfo(version), [version]);
  const downloadLabel = home.download[download.platform];

  return (
    <section className="relative min-h-[86vh] sm:min-h-screen flex items-start justify-center px-6 text-center" style={{ paddingTop: "15vh" }}>
      <div className="max-w-3xl mx-auto">
        <h1
          className="fade-up text-4xl sm:text-5xl md:text-[56px] font-semibold tracking-[-0.02em] leading-[1.1] mb-5"
          style={{ animationDelay: "0.05s", color: "var(--text-primary)" }}
        >
          {home.hero.title}
        </h1>
        <p
          className="fade-up text-base sm:text-lg max-w-md mx-auto leading-relaxed mb-8"
          style={{ animationDelay: "0.15s", color: "var(--text-primary)" }}
        >
          {home.hero.subtitle}
        </p>
        <div
          className="fade-up flex flex-col items-center gap-2"
          style={{ animationDelay: "0.25s" }}
        >
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <a
              href={download.url}
              onClick={() => (window as any).__analytics?.track("download", { platform: download.platform })}
              className="inline-flex items-center gap-2 text-sm font-medium px-7 py-2.5 rounded-full hover:brightness-95 transition whitespace-nowrap"
              style={{ backgroundColor: "#5ba8f5", color: "#fff" }}
            >
              {downloadLabel}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1v8.5M3 7l4 4 4-4M2 12.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <GitHubStarBadge theme={theme} />
          </div>
          <a
            href="https://github.com/0-AI-UG/cate/releases/latest"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs transition-colors"
            style={{ color: "var(--text-primary)", opacity: 0.7 }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
          >
            {home.hero.platforms} · v{version}
          </a>
        </div>
        <HeroNewsletter />
      </div>
    </section>
  );
}

function usePath() {
  return useSyncExternalStore(
    (cb) => { window.addEventListener("popstate", cb); return () => window.removeEventListener("popstate", cb); },
    () => window.location.pathname,
  );
}

function GitHubStarBadge({ theme }: { theme: Theme }) {
  const [stars, setStars] = useState<number | null>(null);

  useEffect(() => {
    // Cached server endpoint — avoids GitHub's 60/hour per-IP unauthenticated limit.
    fetch("/api/stars")
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.count != null) setStars(d.count); })
      .catch(() => {});
  }, []);

  const isDark = theme === "dark";
  const bg = isDark ? "#24292f" : "#fff";
  const fg = isDark ? "#fff" : "#24292f";
  const border = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)";

  return (
    <a
      href="https://github.com/0-AI-UG/cate"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => (window as any).__analytics?.track("github_star_badge_click")}
      className="inline-flex items-center gap-2 text-sm font-medium rounded-full transition-opacity hover:opacity-100"
      style={{ opacity: 0.85, height: 40, padding: "0 16px", backgroundColor: bg, color: fg, border: `1px solid ${border}` }}
    >
      <svg width="18" height="18" viewBox="0 0 16 16" fill={fg}>
        <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0016 8c0-4.42-3.58-8-8-8z" />
      </svg>
      <span>Star on GitHub</span>
      {stars !== null && (
        <span
          className="inline-flex items-center gap-1"
          style={{
            backgroundColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="#f5c542">
            <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.75.75 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25z" />
          </svg>
          {stars >= 1000 ? `${(Math.round(stars / 100) / 10).toFixed(1).replace(/\.0$/, "")}k` : stars}
        </span>
      )}
    </a>
  );
}

function DownloadCTA({ theme }: { theme: Theme }) {
  const { home } = useT();
  const version = useLatestVersion();
  const download = useMemo(() => getDownloadInfo(version), [version]);
  const downloadLabel = home.download[download.platform];

  return (
    <div className="w-full text-center">
      <h2
        className="text-4xl sm:text-5xl font-medium tracking-tight leading-tight mb-8"
        style={{ color: "var(--text-primary)" }}
      >
        {home.downloadCta.titleLines.map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </h2>
      <div className="flex flex-col items-center gap-2">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <a
            href={download.url}
            onClick={() => (window as any).__analytics?.track("download", { platform: download.platform, location: "cta" })}
            className="inline-flex items-center gap-2 text-sm font-medium px-7 py-2.5 rounded-full hover:brightness-95 transition whitespace-nowrap"
            style={{ backgroundColor: "#5ba8f5", color: "#fff" }}
          >
            {downloadLabel}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v8.5M3 7l4 4 4-4M2 12.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
        <a
          href="https://github.com/0-AI-UG/cate/releases/latest"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs transition-colors"
          style={{ color: "var(--text-secondary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
        >
          {home.hero.platforms} · v{version}
        </a>
      </div>
    </div>
  );
}

function Newsletter() {
  const { home } = useT();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === "loading") return;

    setStatus("loading");
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus("error");
        setMessage(data.error || home.newsletter.errorGeneric);
        return;
      }
      setStatus("success");
      setMessage(data.message);
      setEmail("");
      (window as any).__analytics?.track("newsletter_subscribe");
    } catch {
      setStatus("error");
      setMessage(home.newsletter.errorNetwork);
    }
  };

  return (
    <div className="text-center">
        <h2
          className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3"
          style={{ color: "var(--text-primary)" }}
        >
          {home.newsletter.title}
        </h2>
        <p
          className="text-sm sm:text-base mb-6 leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {home.newsletter.body}
        </p>

        {status === "success" ? (
          <p className="text-sm font-medium" style={{ color: "var(--activity-green)" }}>
            {home.newsletter.success}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-row gap-3 justify-center max-w-sm mx-auto">
            <input
              ref={inputRef}
              type="email"
              required
              placeholder={home.newsletter.placeholder}
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
              className="px-4 py-2.5 rounded-full text-sm outline-none transition-all flex-1 min-w-0"
              style={{
                backgroundColor: "var(--surface-4)",
                border: `1px solid ${status === "error" ? "var(--activity-orange)" : "var(--border-strong)"}`,
                color: "var(--text-primary)",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "var(--border-focus)")}
              onBlur={(e) => (e.currentTarget.style.borderColor = status === "error" ? "var(--activity-orange)" : "var(--border-strong)")}
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap hover:brightness-110"
              style={{
                backgroundColor: "#5ba8f5",
                color: "#fff",
                opacity: status === "loading" ? 0.7 : 1,
              }}
            >
              {status === "loading" ? "..." : home.newsletter.subscribe}
            </button>
          </form>
        )}

        {status === "error" && (
          <p className="text-xs mt-2" style={{ color: "var(--activity-orange)" }}>{message}</p>
        )}
    </div>
  );
}

function Unsubscribe() {
  const { home } = useT();
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const email = new URLSearchParams(window.location.search).get("email") || "";

  useEffect(() => {
    if (!email) return;
    setStatus("loading");
    fetch("/api/unsubscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    })
      .then((r) => { setStatus(r.ok ? "done" : "error"); })
      .catch(() => setStatus("error"));
  }, [email]);

  return (
    <section className="flex items-center justify-center px-6" style={{ minHeight: "60vh" }}>
      <div className="max-w-md text-center">
        {!email ? (
          <p style={{ color: "var(--text-secondary)" }}>{home.unsubscribe.noEmail}</p>
        ) : status === "loading" ? (
          <p style={{ color: "var(--text-secondary)" }}>{home.unsubscribe.loading}</p>
        ) : status === "done" ? (
          <>
            <h1 className="text-2xl font-semibold mb-3" style={{ color: "var(--text-primary)" }}>
              {home.unsubscribe.doneTitle}
            </h1>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--text-secondary)" }}>
              {home.unsubscribe.doneBody.replace("{email}", email)}
            </p>
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); window.history.pushState({}, "", "/"); window.dispatchEvent(new PopStateEvent("popstate")); }}
              className="text-sm"
              style={{ color: "var(--focus-blue)" }}
            >
              {home.unsubscribe.back}
            </a>
          </>
        ) : (
          <p style={{ color: "var(--activity-orange)" }}>{home.unsubscribe.error}</p>
        )}
      </div>
    </section>
  );
}

function ArrowButton({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  const { common } = useT();
  const isLeft = side === "left";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isLeft ? common.aria.prevTheme : common.aria.nextTheme}
      className="flex items-center justify-center rounded-full transition-opacity"
      style={{
        width: 26,
        height: 26,
        padding: 0,
        cursor: "pointer",
        color: "var(--text-secondary)",
        backgroundColor: "var(--surface-4)",
        border: "1px solid var(--border-subtle)",
        opacity: 0.6,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
    >
      <svg width="13" height="13" viewBox="0 0 18 18" fill="none">
        <path
          d={isLeft ? "M11 4l-5 5 5 5" : "M7 4l5 5-5 5"}
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function DemoWindow() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = demoShots.length;

  // The previously-shown image stays fully opaque beneath the incoming one so
  // the stack never dips transparent mid-crossfade (only the new image fades in).
  const prevRef = useRef(0);
  const go = (next: number) => setActive((cur) => { if (next !== cur) prevRef.current = cur; return next; });

  // Scroll-linked 3D tilt: the window starts leaning back and rotates up
  // to face the viewer as it scrolls into view. Subtle.
  const tiltRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState(48);
  useEffect(() => {
    const el = tiltRef.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setTilt(0);
      return;
    }
    const MAX = 48; // max lean-back in degrees
    const easeInOut = (t: number) => t * t * (3 - 2 * t); // smoothstep

    const targetTilt = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the window top sits at the bottom of the viewport,
      // 1 once it has risen to ~12% of the viewport height (settled upright).
      // Wider range => the window stands up gradually over more scroll.
      const p = (vh - rect.top) / (vh - vh * 0.12);
      const clamped = Math.max(0, Math.min(1, p));
      return MAX * (1 - easeInOut(clamped));
    };

    let current = targetTilt();
    setTilt(current);

    // Single damped animation loop: current eases toward the scroll target,
    // then the loop parks itself. Restarted on scroll/resize. No CSS
    // transition, so nothing "chases" the frame updates -> no flicker.
    let raf = 0;
    const animate = () => {
      const target = targetTilt();
      current += (target - current) * 0.12;
      if (Math.abs(target - current) < 0.03) {
        current = target;
        setTilt(current);
        raf = 0;
        return;
      }
      setTilt(current);
      raf = requestAnimationFrame(animate);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(animate); };
    window.addEventListener("scroll", kick, { passive: true });
    window.addEventListener("resize", kick, { passive: true });
    return () => {
      window.removeEventListener("scroll", kick);
      window.removeEventListener("resize", kick);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => go((active + 1) % count), 4000);
    return () => clearInterval(id);
  }, [paused, count, active]);

  const step = (dir: 1 | -1) => {
    const next = (active + dir + count) % count;
    go(next);
    (window as any).__analytics?.track("demo_screenshot_click", { screenshot: next + 1 });
  };

  return (
    <div
      ref={tiltRef}
      className="mx-auto"
      style={{ maxWidth: 1152, perspective: 3000 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="relative"
        style={{
          aspectRatio: "3028 / 1902",
          transform: `rotateX(${tilt}deg)`,
          transformOrigin: "center bottom",
          willChange: "transform",
          // Smooth shadow rendered in CSS (follows the window's rounded
          // alpha shape) — the screenshots ship with the baked macOS shadow
          // cropped off, so there's no banded gradient to bend.
          filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.28)) drop-shadow(0 28px 60px rgba(0,0,0,0.4))",
        }}
      >
        {demoShots.map(({ src, label }, i) => {
          const isActive = i === active;
          const isPrev = i === prevRef.current;
          return (
            <img
              key={src}
              src={src}
              alt={`Cate app window showing ${label}`}
              aria-hidden={!isActive}
              loading={i === 0 ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full block"
              style={{
                objectFit: "contain",
                // Active fades in on top; previous stays opaque underneath so
                // the background never shows through during the transition.
                opacity: isActive || isPrev ? 1 : 0,
                zIndex: isActive ? 3 : isPrev ? 2 : 1,
                transition: "opacity 700ms ease",
                pointerEvents: "none",
              }}
            />
          );
        })}
      </div>

      {/* Simple manual controls; the screenshots also advance automatically. */}
      <div
        className="flex items-center justify-center"
        style={{ marginTop: 14, gap: 8 }}
      >
        <ArrowButton side="left" onClick={() => step(-1)} />
        <span
          className="text-xs text-center"
          style={{ color: "var(--text-secondary)", minWidth: 190 }}
        >
          {demoShots[active].label} · {active + 1}/{count}
        </span>
        <ArrowButton side="right" onClick={() => step(1)} />
      </div>
    </div>
  );
}

function SiteFooter() {
  const { common } = useT();
  return (
    /* Footer — sits on the page's site grid, no backdrop */
    <footer className="relative" style={{ zIndex: 3 }}>
      <div className="relative flex flex-col items-center px-6 pt-4">
        <div
          className="mx-auto w-full max-w-2xl rounded-lg px-8 py-10 shadow-2xl"
          style={{ backgroundColor: "var(--surface-3)", border: "1px solid var(--border-subtle)" }}
        >
          <Newsletter />
        </div>
        <div className="w-full mt-20">
          <div className="px-8 sm:px-12 pb-6 pt-6 flex items-center justify-between" style={{ color: "var(--text-secondary)" }}>
            <span className="text-xs font-medium">CATE</span>
            <div className="flex items-center gap-4">
              <a
                href="/blog"
                onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
                className="text-xs font-medium transition-colors"
                style={{ color: "var(--text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
              >
                {common.footer.blog}
              </a>
              <a
                href="/privacy"
                onClick={(e) => { e.preventDefault(); window.history.pushState({}, "", "/privacy"); window.dispatchEvent(new PopStateEvent("popstate")); }}
                className="text-xs font-medium transition-colors"
                style={{ color: "var(--text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
              >
                {common.footer.privacy}
              </a>
              <a
                href="/impressum"
                onClick={(e) => { e.preventDefault(); window.history.pushState({}, "", "/impressum"); window.dispatchEvent(new PopStateEvent("popstate")); }}
                className="text-xs font-medium transition-colors"
                style={{ color: "var(--text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
              >
                {common.footer.impressum}
              </a>
              <a
                href="https://github.com/0-AI-UG/cate"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium transition-colors"
                style={{ color: "var(--text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
              >
                {common.footer.github}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function App() {
  const path = usePath();
  const { theme, toggle } = useTheme();

  if (path === "/unsubscribe") {
    return (
      <>
        <Nav theme={theme} onToggleTheme={toggle} />
        <div className="site-grid-bg min-h-screen" style={{ backgroundColor: "var(--site-bg)", color: "var(--text-primary)" }}>
          <main className="pt-14">
            <Unsubscribe />
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  if (path === "/impressum") {
    return (
      <>
        <Nav theme={theme} onToggleTheme={toggle} />
        <div className="site-grid-bg min-h-screen" style={{ backgroundColor: "var(--site-bg)", color: "var(--text-primary)" }}>
          <main className="pt-14">
            <Impressum />
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  if (path === "/privacy" || path === "/mobile/privacy") {
    return (
      <>
        <Nav theme={theme} onToggleTheme={toggle} />
        <div className="site-grid-bg min-h-screen" style={{ backgroundColor: "var(--site-bg)", color: "var(--text-primary)" }}>
          <main className="pt-14">
            <Privacy mobile={path === "/mobile/privacy"} />
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  if (path === "/blog") {
    return (
      <>
        <Nav theme={theme} onToggleTheme={toggle} />
        <div className="site-grid-bg min-h-screen" style={{ backgroundColor: "var(--site-bg)", color: "var(--text-primary)" }}>
          <main className="pt-14">
            <BlogIndex />
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  if (path.startsWith("/blog/release/")) {
    const version = decodeURIComponent(path.slice("/blog/release/".length).replace(/\/$/, ""));
    return (
      <>
        <Nav theme={theme} onToggleTheme={toggle} />
        <div className="site-grid-bg min-h-screen" style={{ backgroundColor: "var(--site-bg)", color: "var(--text-primary)" }}>
          <main className="pt-14">
            <ReleaseArticlePage version={version} />
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  if (path.startsWith("/blog/")) {
    const slug = decodeURIComponent(path.slice("/blog/".length).replace(/\/$/, ""));
    return (
      <>
        <Nav theme={theme} onToggleTheme={toggle} />
        <div className="site-grid-bg min-h-screen" style={{ backgroundColor: "var(--site-bg)", color: "var(--text-primary)" }}>
          <main className="pt-14">
            <BlogPostPage slug={slug} />
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  return (
    <>
      <Nav theme={theme} onToggleTheme={toggle} />
      <div className="site-grid-bg" style={{ backgroundColor: "var(--site-bg)", color: "var(--text-primary)", overflow: "clip", overflowClipMargin: "120px" }}>
        <div className="relative hero-frame" style={{ zIndex: 1 }}>
          <div
            className="absolute inset-0 bg-cover bg-top bg-no-repeat"
            style={{ backgroundImage: `url(${heroBg})` }}
          />
          <div className="absolute inset-0" style={{ backgroundColor: "var(--site-bg)", opacity: 0.4 }} />
          <div className="relative h-full">
            <Hero theme={theme} />
          </div>
        </div>
        <div style={{ position: "relative", zIndex: 4 }} className="-mt-[20vh] sm:-mt-[32vh] px-4 sm:px-8 md:px-16 lg:px-24">
          <DemoWindow />
        </div>
        <div style={{ position: "relative", zIndex: 3 }}>
          <Manifesto />
          <Testimonials />

          {/* Primary CTA — mission headline + download */}
          <section className="py-24 px-6">
            <DownloadCTA theme={theme} />
          </section>
        </div>

        <SiteFooter />
      </div>
    </>
  );
}

export default App;
